/**
 * Sudoku Game
 * Classic number puzzle game
 */

import React, { useState, useEffect } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Sudoku = ({ onHome }) => {
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [grid, setGrid] = useState(Array(81).fill(0));
  const [solution, setSolution] = useState(Array(81).fill(0));
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useLocalStorage('sudoku_score', 0);
  const [highScore, setHighScore] = useLocalStorage('sudoku_highScore', 0);

  const generatePuzzle = () => {
    const newGrid = Array(81).fill(0);
    // Add some random numbers
    for (let i = 0; i < 20; i++) {
      let pos = Math.floor(Math.random() * 81);
      while (newGrid[pos] !== 0) pos = Math.floor(Math.random() * 81);
      newGrid[pos] = Math.floor(Math.random() * 9) + 1;
    }
    setGrid(newGrid);
    setSolution([...newGrid]);
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const handleCellClick = (index) => {
    if (solution[index] === 0) {
      setSelected(index);
    }
  };

  const handleNumber = (num) => {
    if (selected !== null && solution[selected] === 0) {
      const newGrid = [...grid];
      newGrid[selected] = num;
      setGrid(newGrid);
      setScore((prev) => prev + 10);
    }
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  const isSolved = grid.every((val) => val !== 0);

  return (
    <GameLayout
      title="Sudoku 📊"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Sudoku</h2>
            <p style={styles.menuDescription}>
              Fill the grid with numbers 1-9!
            </p>
            <button
              onClick={generatePuzzle}
              className="btn btn-primary btn-lg"
              style={{ marginTop: '1rem' }}
            >
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <div style={styles.sudokuGrid}>
              {grid.map((val, idx) => (
                <div
                  key={idx}
                  onClick={() => handleCellClick(idx)}
                  style={{
                    ...styles.sudokuCell,
                    backgroundColor: selected === idx ? GAME_CONFIG.colors.primary : 'rgba(0,0,0,0.3)',
                    cursor: solution[idx] === 0 ? 'pointer' : 'default',
                    color: solution[idx] === 0 ? GAME_CONFIG.colors.accent : GAME_CONFIG.colors.accent2,
                    borderRight: ((idx + 1) % 9 === 0) ? 'none' : '1px solid #333',
                    borderBottom: (Math.floor(idx / 9) === 8) ? 'none' : '1px solid #333',
                  }}
                >
                  {val || ''}
                </div>
              ))}
            </div>

            <div style={styles.numberPad}>
              {Array.from({ length: 9 }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => handleNumber(num)}
                  className="btn btn-sm btn-primary"
                  style={styles.numberButton}
                >
                  {num}
                </button>
              ))}
            </div>

            {isSolved && (
              <div style={styles.completeMessage}>
                ✅ Puzzle Completed!
              </div>
            )}
          </>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    fontSize: '1rem',
  },
  sudokuGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(9, 40px)',
    gap: '0',
    padding: '1rem',
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
  },
  sudokuCell: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    border: '1px solid #333',
  },
  numberPad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(9, 1fr)',
    gap: '0.5rem',
    width: '100%',
    maxWidth: '400px',
  },
  numberButton: {
    padding: '0.5rem',
    fontSize: '0.9rem',
  },
  completeMessage: {
    color: GAME_CONFIG.colors.success,
    fontSize: '1.5rem',
    fontWeight: 'bold',
    padding: '1rem',
    backgroundColor: `rgba(72, 187, 120, 0.1)`,
    border: `2px solid ${GAME_CONFIG.colors.success}`,
    borderRadius: GAME_CONFIG.radius.md,
  },
};

export default Sudoku;
